import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { ActivityIndicator } from 'react-native-paper';
import { useAppColors } from '../../hooks/useAppColors';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface PaymentRecord {
  id: string;
  amount: string;
  status: string;
  payment_method: string;
  created_at: string;
  order_number?: string;
}

interface PaymentHistoryListProps {
  payments: PaymentRecord[] | undefined;
  isLoading?: boolean;
  emptyIcon?: string;
  emptyTitle?: string;
}

export default function PaymentHistoryList({
  payments,
  isLoading,
  emptyIcon = 'receipt-text-outline',
  emptyTitle,
}: PaymentHistoryListProps) {
  const { t } = useTranslation();
  const colors = useAppColors();

  if (isLoading) {
    return <ActivityIndicator size="small" color={colors.accent} style={{ marginTop: 24 }} />;
  }

  if (!payments || payments.length === 0) {
    return (
      <View style={styles.emptyState}>
        <Icon name={emptyIcon} size={48} color={colors.textTertiary} />
        <Text style={[styles.emptyTitle, { color: colors.textSecondary }]}>
          {emptyTitle || t('profile.noPaymentHistory')}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.list}>
      {payments.map((payment) => {
        const isCompleted = payment.status === 'completed';
        const isPending = payment.status === 'pending';
        const methodLabel = payment.payment_method === 'vietqr' ? 'VietQR'
          : payment.payment_method === 'onepay_card' ? 'Card'
          : payment.payment_method === 'onepay_token' ? 'Token'
          : payment.payment_method || '—';

        return (
          <View
            key={payment.id}
            style={[styles.paymentRow, { backgroundColor: colors.card, borderColor: colors.border }]}
          >
            <View style={styles.paymentLeft}>
              <View style={[
                styles.paymentStatusDot,
                { backgroundColor: isCompleted ? '#22c55e' : isPending ? '#f59e0b' : '#ef4444' },
              ]} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.paymentAmount, { color: colors.text }]}>
                  {formatCurrency(parseFloat(payment.amount))}
                </Text>
                <Text style={[styles.paymentMeta, { color: colors.textTertiary }]}>
                  {methodLabel} · {formatDate(payment.created_at, t('currentLanguage', { defaultValue: 'en' }))}
                </Text>
                {payment.order_number && (
                  <Text style={[styles.paymentOrder, { color: colors.textTertiary }]}>
                    #{payment.order_number}
                  </Text>
                )}
              </View>
            </View>
            <View style={[
              styles.paymentStatusBadge,
              {
                backgroundColor: isCompleted ? 'rgba(34,197,94,0.12)' : isPending ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
              },
            ]}>
              <Text style={[
                styles.paymentStatusText,
                { color: isCompleted ? '#22c55e' : isPending ? '#f59e0b' : '#ef4444' },
              ]}>
                {isCompleted ? t('paymentStatus.completed') : isPending ? t('paymentStatus.pending') : t('paymentStatus.failed')}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  list: { gap: 12 },
  paymentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
  },
  paymentLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  paymentStatusDot: { width: 8, height: 8, borderRadius: 4 },
  paymentAmount: { fontSize: 15, fontWeight: '600' },
  paymentMeta: { fontSize: 12, marginTop: 2 },
  paymentOrder: { fontSize: 11, marginTop: 1 },
  paymentStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  paymentStatusText: { fontSize: 10, fontWeight: '700' },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 48,
    gap: 8,
  },
  emptyTitle: { fontSize: 16, fontWeight: '600', marginTop: 12 },
});
